import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/client';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import ChecklistPDF from '../pdf/ChecklistPDF';

const PrintItem = ({ item }) => {
  if (item.type === 'heading') return <div className="print-section-heading">{item.text}</div>;
  if (item.type === 'tick') return (
    <div className="print-tick-row">
      <div className="print-checkbox" />
      <div>
        <div className="text-[11pt]">{item.text}</div>
        {item.sub_note && <div className="print-sub-note">{item.sub_note}</div>}
      </div>
    </div>
  );
  if (item.type === 'note') return <div className="print-note">{item.text}</div>;
  if (item.type === 'write_in') return (
    <div className="print-write-in">{item.text}: <span className="print-line" /></div>
  );
  return null;
};

export default function PrintPage() {
  const { runId } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/runs/${runId}/print`)
      .then(r => { setCtx(r.data.print_context); setLoading(false); })
      .catch(() => setLoading(false));
  }, [runId]);

  useEffect(() => {
    if (ctx) {
      const timer = setTimeout(() => window.print(), 500);
      return () => clearTimeout(timer);
    }
  }, [ctx]);

  if (loading) return <div className="p-8 text-center text-sm text-gray-400">Loading print preview...</div>;
  if (!ctx) return <div className="p-8 text-center text-sm text-red-500">Could not load print data</div>;

  const fields = ctx.project_fields || {};
  const fieldLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="print-page" data-testid="print-page">
      <div className="print-header" style={{ borderBottomColor: ctx.print_accent_colour || '#1C3557' }}>
        <div className="flex items-center justify-between">
          {ctx.logo_url && ctx.logo_position === 'top_left' && <img src={ctx.logo_url} alt="Logo" className="h-9 object-contain" />}
          <div className={`${ctx.logo_position === 'top_centre' ? 'text-center flex-1' : ''}`}>
            {ctx.logo_url && ctx.logo_position === 'top_centre' && <img src={ctx.logo_url} alt="Logo" className="h-9 object-contain mx-auto mb-1" />}
            <div className="text-[14pt] font-medium">Project Start Checklist</div>
            <div className="text-[9pt] text-gray-500">{ctx.template_title} {ctx.template_version}</div>
          </div>
          {ctx.logo_url && ctx.logo_position === 'top_right' && <img src={ctx.logo_url} alt="Logo" className="h-9 object-contain" />}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-4 text-[10pt]">
        {Object.entries(fields).map(([key, val]) => (
          <div key={key}><strong>{fieldLabel(key)}:</strong> {val}</div>
        ))}
        <div><strong>Date:</strong> ___________</div>
      </div>

      {(ctx.sections || []).map((section, si) => (
        <div key={si} className="mb-3">
          {section.section_group && <div className="print-group-heading">{section.section_group}</div>}
          {(section.items || []).map((item, ii) => <PrintItem key={ii} item={item} />)}
        </div>
      ))}

      <div className="print-footer">
        <span>Generated by CheckBuild &middot; {fields.project_name}</span>
        <span>{ctx.completed_at ? new Date(ctx.completed_at).toLocaleDateString() : ''}</span>
      </div>

      <div className="no-print mt-6 text-center flex justify-center gap-3">
        <button onClick={() => window.print()} className="px-4 py-2 text-sm text-white rounded-sm" style={{ background: 'var(--brand-primary, #1C3557)' }}>
          Print this page
        </button>
        <button onClick={async () => {
          try {
            let logoBase64 = null;
            if (ctx.logo_url) {
              try {
                const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}${ctx.logo_url}`);
                const blob = await resp.blob();
                logoBase64 = await new Promise(resolve => { const r = new FileReader(); r.onloadend = () => resolve(r.result); r.readAsDataURL(blob); });
              } catch {}
            }
            const doc = <ChecklistPDF printContext={ctx} logoBase64={logoBase64} />;
            const blob = await pdf(doc).toBlob();
            saveAs(blob, `${(ctx.project_fields?.project_name || 'checklist').replace(/\s+/g, '_')}.pdf`);
          } catch (err) { alert('PDF generation failed: ' + err.message); }
        }} data-testid="download-pdf-btn"
          className="px-4 py-2 text-sm border border-gray-300 rounded-sm text-gray-700 hover:bg-gray-50">
          Download PDF
        </button>
      </div>
    </div>
  );
}
